<template>
  <div class="ui form task">
    <div v-if="$store.error" class="error message">
      {{ $store.error }}
    </div>

    <div class="fields">
      <div class="five wide field">
        <input
          ref="input"
          v-model="task.name"
          :placeholder="placeholder"
          class="new-task"
          @focus="onNameFocus"
          @keypress.enter.prevent="submit"
        />
      </div>
      <!-- @keydown.shift.50="showUserSelection" -->

      <!--
        Progressive disclosure (spec §13.2): Calendar/Assignee/Due date
        collapse into a compact row that only appears once the capture
        input has focus or text -- on native, a form panel isn't the
        screen, a capture box is. v-show (not v-if) keeps the fields
        mounted so the jQuery calendar/dropdown bindings set up in
        mounted() below stay attached; only their visibility toggles.
      -->
      <div
        v-show="!isNativeApp || focused || task.name"
        class="native-task-options"
      >
        <div v-if="!task.event_id" class="three wide field">
          <select
            v-model.lazy="task.calendar_id"
            class="ui calendar dropdown selection nowrap"
            data-placeholder="Select Calendar"
          >
            <option v-for="option in calendarsAsOptions" :value="option.value">
              <div
                class="ui left floated red empty circular label"
                :style="{ 'background-color': option.color + ' !important' }"
              />
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="three wide field">
          <select
            ref="userSelection"
            v-model.lazy="task.user_id"
            class="ui dropdown compact selection nowrap"
            data-placeholder="Select User"
          >
            <option v-for="option in usersAsOptions" :value="option.value">
              <avatar
                class="ui image avatar margin-right-mini"
                :username="option.label"
                :src="option.gravatar"
                size="22"
              />
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="three wide field" @keypress.enter.prevent="submit">
          <div ref="calendarPopupTrigger" class="ui field">
            <div class="ui input left icon">
              <i class="calendar link icon" />
              <input
                type="text"
                :value="calendarFormattedDueDate"
                placeholder="Due Date"
                @focus.stop="openCalendarPopup"
              />
            </div>
          </div>
        </div>
      </div>

      <div ref="calendarPopup" class="ui popup">
        <div class="ui calendar" />
        <div v-if="task.event_id || parentEvent" class="field">
          <div class="ui checkbox margin-top-tiny">
            <input v-model="task.relative" type="checkbox" />
            <label>Relative to campaign date</label>
          </div>
        </div>
      </div>

      <!-- Native: Add joins the progressive-disclosure row (spec §13.2) --
           the resting screen is just the capture input, and the primary
           only appears once the user is mid-capture. Same v-show contract
           as .native-task-options above; edit mode (task.id) always shows
           its save/cancel pair. -->
      <div
        v-show="!isNativeApp || task.id || focused || task.name"
        class="one wide computer three wide mobile field"
      >
        <div v-if="!task.id" class="ui icon buttons">
          <button
            v-if="!task.id"
            type="submit"
            class="ui button primary"
            :class="{ loading, disabled: !task.name }"
            @click="submit()"
          >
            Add
          </button>
          <a v-if="allowCancel" class="ui button" @click="removeTask">
            <i class="icon delete padding-top-tiny" />
          </a>
        </div>
        <!-- Native swaps the icon-only pair (floppy disk + minus -- two
             full-width glyph slabs on a phone) for worded Save / quiet
             Cancel; web keeps the compact icon buttons. -->
        <div v-if="task.id" class="ui icon tiny buttons compact">
          <button
            type="submit"
            class="ui button primary"
            :class="{ loading }"
            @click="submit()"
          >
            <i v-if="!isNativeApp" class="save icon" />
            <span v-else>Save</span>
          </button>
          <a v-if="allowCancel" class="ui button" @click="cancelEditing">
            <i v-if="!isNativeApp" class="icon minus padding-top-tiny" />
            <span v-else>Cancel</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dropdown from "components/dropdown.vue";
import store from "store";
import { mapState, mapGetters } from "vuex";
import Avatar from "components/avatar.vue";

// import _ from "lodash";

const { currentUserId, $ } = window;

export default {
  store,
  components: {
    Avatar,
  },
  props: {
    parentEvent: {
      type: Object,
      default: null,
    },
    allowCancel: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: "Task name",
    },
    // users: {
    //   type: Array,
    //   default() {
    //     return [];
    //   },
    // },
    task: {
      type: Object,
      default: null,
    },
    inPlace: false,
  },
  data() {
    return {
      loading: false,
      showUserDropdown: false,
      focused: false,
    };
  },
  methods: {
    submit() {
      if (this.task.id) {
        this.updateTask();
      } else {
        this.addTask();
      }
    },
    removeTask() {
      this.$emit("removeTask");
    },
    // event.isTrusted is false for the programmatic focusName() re-focus
    // that follows a successful add -- without this check, refocusing the
    // cleared input would immediately re-expand the options row it was
    // just collapsed by resetting `focused` (spec §13.2).
    onNameFocus(event) {
      if (event.isTrusted) {
        this.focused = true;
      }
    },
    cancelEditing() {
      this.$emit("cancelEditing");
    },
    changeUser(value, text) {
      if (value && text) {
        this.task.user_login = text;
        this.task.user_id = value;
      } else if (text == "Any User") {
        this.task.user_login = null;
        this.task.user_id = null;
      }
    },
    addTask() {
      this.hideCalendarPicker();
      this.loading = true;
      this.$store
        .dispatch("task/create", { item: this.task, inPlace: this.inPlace })
        .then(() => {
          if (!this.inPlace) {
            this.clear();
            // Collapse the options row again (spec §13.2's "progressive")
            // for the next capture.
            this.focused = false;
          }
          this.focusName();
          this.loading = false;
        });
    },
    updateTask() {
      this.hideCalendarPicker();
      this.loading = true;
      this.$store.dispatch("task/update", this.task).then(() => {
        this.loading = false;
        this.cancelEditing();
      });
    },
    focusName() {
      this.$el.querySelector("input.new-task").focus();
    },
    clearUserDropdown() {
      $(this.$refs.userSelection).dropdown("clear");
    },
    hideCalendarPicker() {
      $(this.$el).find(".calendar.field").calendar("blur");
    },
    clearCalendarPicker() {
      $(this.$el).find(".calendar.field").calendar("clear");
    },
    clear() {
      this.hideCalendarPicker();
      this.clearCalendarPicker();
      this.clearUserDropdown();
    },
    initCalendarPopup() {
      $(this.$refs.calendarPopupTrigger).popup({
        popup: $(this.$refs.calendarPopup),
        position: "bottom center",
        on: "click",
      });
    },
    openCalendarPopup() {
      $(this.$el).find(this.$refs.calendarPopupTrigger).popup("show");
      $(this.$refs.calendarPopup).find(".ui.calendar").calendar("focus");
    },
    closeCalendarPopup() {
      $(this.$el).find(this.$refs.calendarPopupTrigger).popup("hide");
    },
    initCalendar() {
      const $this = this;
      const dueOnPicker = $(this.$refs.calendarPopup).find(".ui.calendar");

      dueOnPicker.calendar({
        inline: true,
        type: "date",
        initialDate: $this.parentEvent
          ? window.moment($this.parentEvent.start).toDate()
          : null,
        onChange(date, text) {
          $this.$set($this.task, "due_on", text);
          $this.closeCalendarPopup();
        },
      });

      this.initCalendarPopup();
    },
    initDropdowns() {
      $(this.$el)
        .find(".dropdown.selection:not(.no-js)")
        .each((i, element) => {
          if (!$(element).data("dropdown-loaded")) {
            $(element)
              .dropdown({
                fullTextSearch: true,
                placeholder: $(element).data("placeholder") || "auto",
              })
              .closest(".dropdown")
              .data("dropdown-loaded", true);
          }
        });
    },
  },
  computed: {
    ...mapState("calendar", {
      calendars: "calendars",
    }),
    ...mapGetters("calendar", {
      users: "users",
    }),
    calendarFormattedDueDate() {
      if (!this.task.due_on || this.task.due_on === "") {
        return "";
      }
      return moment(this.task.due_on).format("MMMM D, YYYY");
    },
    usersAsOptions() {
      return this.users.map((user) => ({
        label: user.login,
        value: user.id,
        gravatar: user.gravatar,
      }));
    },
    calendarsAsOptions() {
      return this.calendars.map((calendar) => ({
        label: calendar.name,
        value: calendar.id,
        color: calendar.color,
      }));
    },
    currentUserId() {
      return currentUserId;
    },
    isNativeApp() {
      return document.documentElement.hasAttribute("data-hotwire-native");
    },
  },
  mounted() {
    this.initCalendar();
    this.initDropdowns();
  },
  updated() {},
};
</script>

<!-- <style src="tributejs/dist/tribute.css"></style> -->
<style scoped>
.ui.selection.dropdown .text {
  white-space: nowrap !important;
}

/* display: contents keeps .native-task-options out of the box model
   entirely so its three .field children stay direct children of .fields
   for Fomantic's (and index.vue's mobile ::v-deep) CSS -- v-show's
   inline display:none still hides the whole group when collapsed. */
.native-task-options {
  display: contents;
}

/* Native-only compact chip row (spec §13.2) -- 36px controls instead of
   full desktop dropdown fields. Un-gated on the web so mobile web (same
   <768px breakpoint) doesn't regress; this only ever renders once
   revealed there too, which is an improvement, not a behavior change. */
@media (max-width: 768px) {
  .native-task-options .field {
    margin-bottom: 0 !important;
  }

  .native-task-options ::v-deep .ui.selection.dropdown,
  .native-task-options .ui.input.left.icon input {
    min-height: 36px !important;
    font-size: 14px !important;
    border-radius: 18px !important;
  }
}

/* One confident primary (assessment: Tasks #1). Add is v-show-hidden on
   native until the input has focus or text; in the brief focused-but-empty
   moment it waits as a full-opacity quiet coral tint (palette tokens) and
   flips solid the moment it's actionable -- a 40%-alpha coral slab at 48px
   reads dead/broken, the exact anti-pattern the spec named. Full-width
   48px only when Add is the button group's only child (the header capture
   row has no Cancel sibling). */
@media (max-width: 768px) {
  .ui.icon.buttons > .ui.button.primary:only-child {
    width: 100%;
    min-height: 48px;
    font-size: 17px;
  }

  html[data-hotwire-native] .ui.icon.buttons > .ui.button.primary.disabled {
    opacity: 1 !important;
    background-color: rgba(244, 128, 111, 0.15) !important;
    color: var(--color-primary-hover, #e06b5a) !important;
  }

  /* In-place editor: solid Save is the row's one primary; Cancel is a
     quiet text action, not a second gray slab. Stacked full-width by the
     parent's mobile rules, each button re-earns the theme's full 0.5rem
     radius (Fomantic's horizontal-group corner logic left Save square on
     the right). */
  html[data-hotwire-native] .ui.icon.tiny.buttons.compact > .ui.button {
    border-radius: 0.5rem !important;
  }

  html[data-hotwire-native] .ui.icon.tiny.buttons.compact > a.ui.button {
    background: transparent !important;
    box-shadow: none !important;
    color: var(--text-secondary, rgba(0, 0, 0, 0.6));
  }
}

input::-webkit-input-placeholder {
  font-style: italic;
  font-weight: 300;
  color: var(--text-disabled);
}
input::-moz-placeholder {
  font-style: italic;
  font-weight: 300;
  color: var(--text-disabled);
}
input::input-placeholder {
  font-style: italic;
  font-weight: 300;
  color: var(--text-disabled);
}
</style>
